import React, { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Link } from 'react-router-dom';
import ApiCall from '../../utils/Api';
import Web3 from 'web3';
import {convertDateFormat,extractTimeFromTimestamp} from "../../utils/services";

export default function ProviderDetailsTable({data}) {
  const [web3, setWeb3] = useState(null);
  const [ethPrice, setEthPrice] = useState(null);
  const [lastTransactions, setLastTransactions] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await ApiCall.Api.get('/thirdPartyApi/getCryptoData');
        const ethereumData = response.data.data['1027'];
        const usdPrice = ethereumData.quote.USD.price;
        setEthPrice(usdPrice);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (window.ethereum) {
      setWeb3(new Web3(window.ethereum));
    } else if (window.web3) {
      setWeb3(new Web3(window.web3.currentProvider));
    }
  }, []);

  useEffect(() => {
    async function fetchLastTransactions() {
      if (!data) return;
      const results = {};
      await Promise.all(
        data.map(async (row) => {
          let walletAddress = row?.BuyerConsumer?.walletAddress?.[0] || row?.BuyerProvider?.walletAddress?.[0];
          if (!walletAddress) return;
          try {
            const response = await ApiCall.SECURE_API.get(`/api/latestTransaction?walletAddress=${walletAddress}`);
            if (response.data && response.data.data) {
              results[walletAddress] = response.data.data;
            }
          } catch (error) {
            results[walletAddress] = null;
          }
        })
      );
      setLastTransactions(results);
    }
    fetchLastTransactions();
  }, [data]);

  const weiToEth = (wei) => Number(wei || 0) / 1e18;
  const formatEth = (eth) => `${eth.toFixed(6)} ETH`;
  const formatUsd = (eth) => (ethPrice ? `${(eth * ethPrice).toFixed(0)} USD` : '-');
  // Show only last 8 digits of wallet address
  const maskWallet = (addr) => (addr && addr.length >= 8 ? addr.slice(-8) : addr || '-');
  
  // Format TDUX amount with commas
  const formatTDUX = (amount) => {
    if (!amount || amount === 0) return '0';
    return Number(amount).toLocaleString();
  };
  const statusBadge = (statusBool) => (
    <span style={{
      background: statusBool ? 'rgba(47, 183, 77, 0.13)' : 'rgba(155, 155, 155, 0.13)',
      color: statusBool ? 'rgba(47, 183, 77, 1)' : 'rgba(155, 155, 155, 1)',
      padding: 8,
      borderRadius: 6,
      display: 'inline-block'
    }}>
      {statusBool ? 'Active' : 'Inactive'}
    </span>
  );

  return (
    <TableContainer style={{borderRadius:9}} component={Paper}>
      <Table sx={{ minWidth: 650 }} aria-label="fund subscribers">
        <TableHead style={{backgroundColor:'Black'}}>
          <TableRow>
            <TableCell align="center" style={{color:'white'}}>Joined Fund on</TableCell>
            <TableCell align="center" style={{color:'white'}}>Last Purchase</TableCell>
            <TableCell align="center" style={{color:'white'}}>Name</TableCell>
            <TableCell align="center" style={{color:'white'}}>Wallet Address</TableCell>
            <TableCell align="center" style={{color:'white'}}>New Email</TableCell>
            <TableCell align="center" style={{color:'white'}}>Status</TableCell>
            <TableCell align="center" style={{color:'white'}}>Total Purchase Price</TableCell>
            <TableCell align="center" style={{color:'white'}}>Total Investment</TableCell>
            <TableCell align="center" style={{color:'white'}}>Yield Balance</TableCell>
            <TableCell align="center" style={{color:'white'}}>Balance</TableCell>
            <TableCell align="center" style={{color:'white'}}>Transaction</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(!data || data.length === 0) ? (
            <TableRow>
              <TableCell align="center">-</TableCell>
              <TableCell align="center">-</TableCell>
              <TableCell align="center">-</TableCell>
              <TableCell align="center">-</TableCell>
              <TableCell align="center">-</TableCell>
              <TableCell align="center">-</TableCell>
              <TableCell align="center">-</TableCell>
              <TableCell align="center">-</TableCell>
              <TableCell align="center">-</TableCell>
              <TableCell align="center">-</TableCell>
              <TableCell align="center">-</TableCell>
            </TableRow>
          ) : data.map((row, index) => {
            // Debug logging for each row
            if (index === 0) {
              console.log("🚀 ~ ProviderDetailsTable ~ First row data:", row);
              console.log("🚀 ~ ProviderDetailsTable ~ Row keys:", Object.keys(row));
              console.log("🚀 ~ ProviderDetailsTable ~ Has BuyerConsumer:", !!row.BuyerConsumer);
              console.log("🚀 ~ ProviderDetailsTable ~ Has BuyerProvider:", !!row.BuyerProvider);
            }
            // Get wallet address - prefer purchase WalletAddress, then BuyerConsumer walletAddress
            const walletAddress = row?.WalletAddress || 
                                  (Array.isArray(row?.BuyerConsumer?.walletAddress) ? row.BuyerConsumer.walletAddress[0] : row?.BuyerConsumer?.walletAddress) ||
                                  (Array.isArray(row?.BuyerProvider?.walletAddress) ? row.BuyerProvider.walletAddress[0] : row?.BuyerProvider?.walletAddress) ||
                                  '-';
            const lastTx = lastTransactions[walletAddress];
            
            // Get buyer name - ONLY use BuyerConsumer (Fund Subscriber), NOT BuyerProvider
            // This table is for Fund Subscribers, so we should only show subscriber data
            const buyer = row?.BuyerConsumer; // Only use BuyerConsumer, not BuyerProvider
            const firstName = buyer?.firstName || '';
            const lastName = buyer?.lastName || '';
            
            // Build name: ONLY from fund subscriber firstName/lastName
            // Don't use provider institutionName here (this is for subscribers only)
            let name = '-';
            if (firstName || lastName) {
              name = `${firstName || ''} ${lastName || ''}`.trim();
              if (!name || name === ' ') name = '-'; // Don't show empty or spaces
            }
            
            // Debug log for first row
            if (index === 0) {
              console.log('🔍 Name Debug:', {
                buyer: buyer,
                firstName: firstName,
                lastName: lastName,
                name: name
              });
            }
            
            // Get email from buyer (ONLY from BuyerConsumer/Fund Subscriber)
            const email = buyer?.email || '-';
            
            // Get status from buyer (fund subscribers use isVerified, not status)
            const statusBool = buyer?.isVerified ?? buyer?.status ?? false;
            
            // Get purchase price from the purchase record (priceWei or totalWei)
            const purchasePriceWei = row?.priceWei || row?.totalWei || '0';
            const totalEth = weiToEth(purchasePriceWei);
            const totalUsd = formatUsd(totalEth);
            
            // Get transaction amount in TDUX - convert from priceWei/totalWei or use ticket price
            // Try to get from selectedLevel first (actual purchase level), then dataUnit pricingLevels
            let transactionAmount = 0;
            
            // First priority: selectedLevel ticketUSD (if it's in USD, we need to convert to TDUX)
            if (row?.selectedLevel?.ticketUSD) {
              transactionAmount = Number(row.selectedLevel.ticketUSD);
            }
            // Second priority: selectedLevel ticketETH (convert to USD/TDUX using ETH price)
            else if (row?.selectedLevel?.ticketETH && ethPrice) {
              transactionAmount = Number(row.selectedLevel.ticketETH) * ethPrice;
            }
            // Third priority: Find matching level in pricingLevels
            else if (row?.selectedLevel?.levelName && row?.dataUnit?.pricingLevels) {
              const matchingLevel = row.dataUnit.pricingLevels.find(level => 
                level.levelName === row.selectedLevel.levelName || 
                level.levelName?.toString() === row.selectedLevel.levelName?.toString()
              );
              if (matchingLevel?.ticketUSD) {
                transactionAmount = Number(matchingLevel.ticketUSD);
              } else if (matchingLevel?.ticketETH && ethPrice) {
                transactionAmount = Number(matchingLevel.ticketETH) * ethPrice;
              }
            }
            // Fourth priority: First pricing level
            else if (row?.dataUnit?.pricingLevels && row.dataUnit.pricingLevels.length > 0) {
              const firstLevel = row.dataUnit.pricingLevels[0];
              if (firstLevel.ticketUSD) {
                transactionAmount = Number(firstLevel.ticketUSD);
              } else if (firstLevel.ticketETH && ethPrice) {
                transactionAmount = Number(firstLevel.ticketETH) * ethPrice;
              }
            }
            // Fallback: Use purchase price in ETH converted to USD
            else if (totalEth > 0 && ethPrice) {
              transactionAmount = totalEth * ethPrice;
            }
            
            // Get yield balance - calculate from annualYieldPercent of the selected level
            // Check selectedLevel first, then dataUnit pricingLevels
            let annualYieldPercent = 0;
            
            // First try: selectedLevel has annualYieldPercent
            if (row?.selectedLevel?.annualYieldPercent) {
              annualYieldPercent = row.selectedLevel.annualYieldPercent;
            } 
            // Second try: Find matching level in dataUnit pricingLevels by levelName
            else if (row?.selectedLevel?.levelName && row?.dataUnit?.pricingLevels) {
              const matchingLevel = row.dataUnit.pricingLevels.find(level => 
                level.levelName === row.selectedLevel.levelName || 
                level.levelName?.toString() === row.selectedLevel.levelName?.toString()
              );
              if (matchingLevel?.annualYieldPercent) {
                annualYieldPercent = matchingLevel.annualYieldPercent;
              }
            }
            // Third try: Use first pricing level if no selectedLevel
            else if (row?.dataUnit?.pricingLevels && row.dataUnit.pricingLevels.length > 0) {
              annualYieldPercent = row.dataUnit.pricingLevels[0]?.annualYieldPercent || 0;
            }
            
            // Debug log for first row
            if (index === 0) {
              console.log('🔍 Yield Balance Debug:', {
                hasSelectedLevel: !!row?.selectedLevel,
                selectedLevel: row?.selectedLevel,
                selectedLevelYield: row?.selectedLevel?.annualYieldPercent,
                selectedLevelTicketUSD: row?.selectedLevel?.ticketUSD,
                selectedLevelTicketETH: row?.selectedLevel?.ticketETH,
                hasPricingLevels: !!row?.dataUnit?.pricingLevels,
                pricingLevels: row?.dataUnit?.pricingLevels,
                pricingLevelsCount: row?.dataUnit?.pricingLevels?.length || 0,
                annualYieldPercent: annualYieldPercent,
                transactionAmount: transactionAmount,
                totalEth: totalEth,
                ethPrice: ethPrice,
                purchaseDate: row?.purchaseDate || row?.lastTransactionDate
              });
            }
            
            // Calculate yield balance: (transactionAmount * annualYieldPercent / 100) * (days since purchase / 365)
            let yieldBalance = '0';
            if (annualYieldPercent > 0 && transactionAmount > 0) {
              const purchaseDate = new Date(row?.purchaseDate || row?.lastTransactionDate || row?.createdAt || row?.BuyerConsumer?.createdAt);
              const daysSincePurchase = Math.max(0, (new Date() - purchaseDate) / (1000 * 60 * 60 * 24));
              const yearlyYield = (transactionAmount * annualYieldPercent) / 100;
              yieldBalance = (yearlyYield * daysSincePurchase / 365).toFixed(2); // Show 2 decimals
              
              if (index === 0) {
                console.log('✅ Calculated Yield Balance:', {
                  transactionAmount: transactionAmount,
                  annualYieldPercent: annualYieldPercent,
                  daysSincePurchase: daysSincePurchase.toFixed(2),
                  yearlyYield: yearlyYield.toFixed(4),
                  yieldBalance: yieldBalance
                });
              }
            } else {
              if (index === 0) {
                console.log('⚠️ Yield Balance is 0 because:', {
                  annualYieldPercent: annualYieldPercent,
                  transactionAmount: transactionAmount,
                  reason: !annualYieldPercent ? 'annualYieldPercent is 0' : 'transactionAmount is 0'
                });
              }
            }
            
            // Get total balance (transaction amount + yield balance)
            const balance = row?.totalBalance || 
              (Number(transactionAmount) + Number(yieldBalance)).toString() || '0';
            
            // Get subscriber ID for the link - try multiple possible locations
            let subscriberId = '';
            
            // Helper function to extract ID safely
            const extractId = (value) => {
              if (!value) return '';
              if (typeof value === 'string') return value;
              if (typeof value === 'object') {
                if (value._id) {
                  return typeof value._id === 'object' ? value._id.toString() : value._id;
                }
                if (value.toString) return value.toString();
              }
              return String(value);
            };
            
            // Try different possible ID locations in order of priority
            subscriberId = extractId(row?.BuyerConsumer?._id) ||
                          extractId(row?.BuyerProvider?._id) ||
                          extractId(row?.buyerConsumer?._id) ||
                          extractId(row?.buyerProvider?._id) ||
                          extractId(row?.BuyerConsumer) ||
                          extractId(row?.BuyerProvider) ||
                          extractId(row?._id); // Fallback to row's own _id
            
            // Debug log to check ID availability
            if (index === 0) {
              console.log('🔍 ID Debug - Row data:', {
                hasBuyerConsumer: !!row?.BuyerConsumer,
                hasBuyerProvider: !!row?.BuyerProvider,
                BuyerConsumerType: typeof row?.BuyerConsumer,
                BuyerProviderType: typeof row?.BuyerProvider,
                BuyerConsumer_id: row?.BuyerConsumer?._id,
                BuyerProvider_id: row?.BuyerProvider?._id,
                extractedId: subscriberId,
                rowKeys: Object.keys(row || {})
              });
              
              if (!subscriberId) {
                console.warn('⚠️ No subscriber ID found in row:', row);
              }
            }
            
            return (
              <TableRow key={row._id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell align="center">
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-start' }}>
                    <span style={{ fontWeight:600 }}>
                      {convertDateFormat(row?.purchaseDate || row?.lastTransactionDate || row?.createdAt || row?.BuyerConsumer?.createdAt || row?.BuyerProvider?.createdAt)}
                    </span>
                    <span style={{ color:'gray' }}>
                      {extractTimeFromTimestamp(row?.purchaseDate || row?.lastTransactionDate || row?.createdAt || row?.BuyerConsumer?.createdAt || row?.BuyerProvider?.createdAt)}
                    </span>
                  </div>
                </TableCell>
                <TableCell align="center">
                  {lastTx ? (
                    <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-start' }}>
                      <span style={{ fontWeight:600 }}>{convertDateFormat(lastTx?.date)}</span>
                      <span style={{ color:'gray' }}>{extractTimeFromTimestamp(lastTx?.date)}</span>
                    </div>
                  ) : (
                    <span style={{ color:'gray' }}>-</span>
                  )}
                </TableCell>
                <TableCell align="center">{name}</TableCell>
                <TableCell align="center">
                  {walletAddress && walletAddress !== '-' ? maskWallet(walletAddress) : '-'}
                </TableCell>
                <TableCell align="center">{email}</TableCell>
                <TableCell align="center">{statusBadge(!!statusBool)}</TableCell>
                <TableCell align="center">
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-start' }}>
                    <span style={{ fontWeight:600 }}>{formatEth(totalEth)}</span>
                    <span style={{ color:'gray' }}>{totalUsd}</span>
                  </div>
                </TableCell>
                <TableCell align="center">
                  <span style={{ fontWeight: 500 }}>
                    {formatTDUX(transactionAmount)} TDUX
                  </span>
                </TableCell>
                <TableCell align="center">
                  <span style={{ fontWeight: 500 }}>
                    {formatTDUX(yieldBalance)} TDUX
                  </span>
                </TableCell>
                <TableCell align="center">
                  <span style={{ fontWeight: 500 }}>
                    {formatTDUX(balance)} TDUX
                  </span>
                </TableCell>
                <TableCell align="center">
                  {subscriberId ? (
                    <Link 
                      to={`/admin-panel/FundSubscriberTransactions/${subscriberId}`}
                      style={{ color: '#9c27b0' }}
                    >
                      View Detail
                    </Link>
                  ) : (
                    <span style={{ color: '#999', fontSize: '12px' }}>No ID available</span>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}